import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { EmptyState } from '~/shared-components';
import ArrowRightIcon from '@bufferapp/ui/Icon/Icons/ArrowRight';
import {
  selectCampaignCounts,
  selectCurrentCampaign,
} from '~/campaign/selectors';
import { campaignCreate } from '~/routes';
import { CampaignTab, CtaLocation } from '~/campaign/types';

import { actions as campaignActions } from '../../../reducer';
import EmptyCampaignBanner from '../EmptyCampaignBanner';
import ExamplePost from '../ExamplePost';

const EmptyCampaignWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;

  > * {
    flex-basis: 100%;
  }
`;

type EmptyStateCampaignProps = {
  hideAnalyzeReport?: boolean;
};

const EmptyStateCampaign = ({
  hideAnalyzeReport,
}: EmptyStateCampaignProps): JSX.Element => {
  const dispatch = useDispatch();
  const campaign = useSelector(selectCurrentCampaign, shallowEqual);
  const { tab } = useParams() as { tab?: CampaignTab };
  const { sent, scheduled, draftsCount, approvalsCount } =
    useSelector(selectCampaignCounts);
  const displayEmptyCampaign =
    tab === CampaignTab.Scheduled && scheduled === 0 && sent === 0;
  const displayEmptySentPosts = tab === CampaignTab.Sent && sent === 0;
  const displayAllPostsSent =
    tab === CampaignTab.Scheduled && scheduled === 0 && sent > 0;
  const displayEmptyDraftsTab = tab === CampaignTab.Drafts && draftsCount === 0;
  const displayEmptyApprovalsTab =
    tab === CampaignTab.Approvals && approvalsCount === 0;

  const { t } = useTranslation();

  const teamMemberPrimaryAction = {
    label: t('campaigns.viewCampaign.allPostsSent.createCampaign'),
    onClick: () => dispatch(campaignCreate.goTo()),
  };
  const ownerPrimaryAction = {
    label: t('campaigns.viewCampaign.allPostsSent.viewReport'),
    onClick: () => dispatch(campaignActions.goToAnalyzeReport(campaign)),
    icon: <ArrowRightIcon />,
    iconEnd: true,
  };

  return (
    <main id="main">
      <EmptyCampaignWrapper>
        {displayEmptyCampaign && (
          <>
            <EmptyCampaignBanner
              onCreatePostClick={() => dispatch(campaignActions.createPost())}
            />
            <ExamplePost />
            <ExamplePost />
          </>
        )}
        {displayEmptySentPosts && (
          <EmptyState
            height="100%"
            title={t('campaigns.viewCampaign.emptySentPosts.title')}
            subtitle={t('campaigns.viewCampaign.emptySentPosts.subtitle')}
            heroImg="https://buffer-publish.s3.amazonaws.com/images/campaign-not-sent.png"
          />
        )}
        {displayEmptyDraftsTab && (
          <>
            <EmptyState
              height="100%"
              title={t('campaigns.viewCampaign.emptyDrafts.title')}
              subtitle={t('campaigns.viewCampaign.emptyDrafts.subtitle')}
              heroImg="https://buffer-publish.s3.amazonaws.com/images/campaigns-drafts-1.png"
              primaryAction={{
                label: t('campaigns.viewCampaign.emptyDrafts.createDraft'),
                onClick: () =>
                  dispatch(
                    campaignActions.createDraft(CtaLocation.EmptyDraftsTab)
                  ),
              }}
            />
          </>
        )}
        {displayEmptyApprovalsTab && (
          <EmptyState
            height="100%"
            title={t('campaigns.viewCampaign.emptyApprovals.title')}
            subtitle={t('campaigns.viewCampaign.emptyApprovals.subtitle')}
            heroImg="https://buffer-publish.s3.amazonaws.com/images/campaigns-drafts-1.png"
          />
        )}
        {displayAllPostsSent && (
          <EmptyState
            height="100%"
            title={t('campaigns.viewCampaign.allPostsSent.title')}
            subtitle={t('campaigns.viewCampaign.allPostsSent.subtitle')}
            heroImg="https://buffer-publish.s3.amazonaws.com/images/campaign-sent-1.png"
            secondaryAction={{
              label: t('campaigns.viewCampaign.allPostsSent.createPost'),
              onClick: () =>
                dispatch(
                  campaignActions.createPost(CtaLocation.EmptyScheduledTab)
                ),
            }}
            primaryAction={
              hideAnalyzeReport ? teamMemberPrimaryAction : ownerPrimaryAction
            }
          />
        )}
      </EmptyCampaignWrapper>
    </main>
  );
};

EmptyStateCampaign.defaultProps = {
  hideAnalyzeReport: false,
};

export default EmptyStateCampaign;
